import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight, Heart } from "lucide-react";
import { subscribeNewsletter } from "../lib/api";
import { KIDS, HARD_STATS, WELCOME_IMG, HOPE_IMG, CHALLENGE_BANNER } from "../data/kids";
import { HOME_VIDEOS } from "../data/videos";
import InstagramFeed from "../components/InstagramFeed";
import KidCardMedia from "../components/KidCardMedia";
import SEO from "../components/SEO";

const VOL1_COVER = "https://customer-assets.emergentagent.com/job_redemption-songs/artifacts/u1pk93uo_FosterCareAnthem_VOL1_Cover.png";
const WCFY_COVER = "https://customer-assets.emergentagent.com/job_redemption-songs/artifacts/kd42ok0u_FosterCareAnthem_WereComingForYou_2026.png";

export default function Home() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="home-page">
      <SEO
        path="/"
        title="Foster Care Awareness 365 | Year-Round Advocacy, Resources & Support"
        description="Foster Care Awareness 365 is your year-round hub for foster care education, advocacy, and action. Explore resources for foster parents, foster youth, statistics, and the Foster Care Anthem Project. 391,000+ children need champions every day."
      />
      {/* HERO — Welcome Home */}
      <section className="relative overflow-hidden border-b border-fc-border bg-fc-ink">
        {/* Cinematic background — FULL color, no white-wash */}
        <div
          className="absolute inset-0 ken-burns"
          style={{ backgroundImage: `url(${WELCOME_IMG})`, backgroundSize: "cover", backgroundPosition: "center 30%" }}
          aria-hidden="true"
        />
        {/* Dark scrim — left-heavy so headline + body text reads crisp white */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/25" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-b from-transparent to-black/70" aria-hidden="true" />

        <div className="container-xl relative pt-20 pb-24 md:pt-28 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-9 fade-up">
            <div className="overline mb-6 text-fc-accent" data-testid="hero-eyebrow">Advocacy · Story · Music · Est. 2025</div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[108px] leading-[0.92] tracking-tight text-white">
              Welcome <span className="italic text-fc-accent">Home.</span>
            </h1>
            <p className="mt-6 font-display text-2xl md:text-3xl text-white max-w-3xl leading-tight">
              Supporting foster kids across our community — <em className="text-fc-accent">365 days a year.</em>
            </p>
            <p className="mt-6 text-base md:text-lg max-w-2xl text-white/85 leading-relaxed">
              A tremendous opportunity to shine a light every single day on the nearly 500,000 children in the U.S. foster care system. Each child deserves a family, a safe home, and someone who shows up. These children need you to take action.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/meet-the-kids" className="btn-primary" data-testid="hero-cta-kids" style={{background:"#F9F9F6", color:"#121212", borderColor:"#F9F9F6"}}>
                Meet The Kids <ArrowUpRight size={16} />
              </Link>
              <Link to="/get-involved" className="btn-outline" data-testid="hero-cta-give" style={{borderColor:"#F9F9F6", color:"#F9F9F6"}}>
                Take Action
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* HARD STATS */}
      <section className="bg-fc-ink text-fc-bg">
        <div className="container-xl py-16">
          <div className="overline mb-6 opacity-70">The Why · Shocking Stats That Demand Action</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-0 border-t border-l border-fc-bg/15">
            {HARD_STATS.map((s, i) => (
              <div key={i} className="p-6 md:p-7 border-r border-b border-fc-bg/15">
                <div className="font-display text-3xl md:text-4xl text-fc-accent leading-none mb-3">{s.num}</div>
                <div className="text-xs md:text-sm leading-snug opacity-80">{s.label}</div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm opacity-70 max-w-2xl">
            These aren't just numbers. They're urgent calls to act — <em className="text-fc-accent not-italic">365 days a year.</em>
          </p>
        </div>
      </section>

      {/* MEET THE KIDS */}
      <section id="meet-the-kids" className="bg-fc-surface border-b border-fc-border">
        <div className="container-xl py-20 md:py-28">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="overline mb-3 text-fc-muted">Featured Profiles · Real Stories</div>
              <h2 className="font-display text-4xl md:text-6xl leading-none">Meet the children <br/><span className="italic">behind the cause.</span></h2>
              <p className="mt-5 text-fc-muted max-w-2xl">
                Learn their hopes, interests, and how you can be the one who gives them a forever family. These four featured profiles are archetypes based on national trends — every detail echoes a real child waiting right now.
              </p>
            </div>
            <Link to="/meet-the-kids" className="btn-outline" data-testid="home-kids-all">View All Profiles</Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {KIDS.map((k) => (
              <Link key={k.slug} to={`/meet-the-kids/${k.slug}`} className="group block bg-fc-bg border border-fc-border card-hover overflow-hidden" data-testid={`home-kid-${k.slug}`}>
                <KidCardMedia
                  image={k.hero}
                  alt={`Meet ${k.name}`}
                  youtubeId={k.youtube_id}
                  className="aspect-[3/4]"
                  testId={`home-kid-media-${k.slug}`}
                />
                <div className="p-5 border-t border-fc-border">
                  <div className="overline text-fc-accent mb-1">Age {k.age}</div>
                  <div className="font-display text-2xl mb-2">Meet {k.name}</div>
                  <div className="text-sm text-fc-muted leading-snug mb-4">"{k.tagline}"</div>
                  <div className="flex items-center gap-2 overline text-fc-ink">
                    Read {k.name}'s Story <ArrowUpRight size={14} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* EVERYDAY AWARENESS · 365 CHALLENGE */}
      <section className="container-xl py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="overline text-fc-muted mb-4">Everyday Awareness · 31 Days of Action</div>
            <h2 className="font-display text-4xl md:text-6xl leading-none">The <span className="italic text-fc-accent">#Foster365</span> Challenge.</h2>
            <p className="mt-6 text-fc-muted leading-relaxed">
              Every day counts. Every child matters. Take one small action each day — <strong className="text-fc-ink">Learn · Share · Give · Act · Spotlight</strong> — and turn a single month into lifelong habit.
            </p>
            <div className="mt-8 grid grid-cols-5 gap-2">
              {[
                { i: "📖", t: "Learn" }, { i: "💬", t: "Share" }, { i: "🤝", t: "Give" }, { i: "❤️", t: "Act" }, { i: "📸", t: "Spotlight" },
              ].map((c) => (
                <div key={c.t} className="text-center border border-fc-border bg-fc-surface py-4">
                  <div className="text-xl">{c.i}</div>
                  <div className="overline mt-1">{c.t}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex gap-3 flex-wrap">
              <Link to="/everyday-awareness" className="btn-primary" data-testid="home-awareness-link">Join the Challenge</Link>
              <Link to="/get-involved" className="btn-outline" data-testid="home-give-link">Ways to Give</Link>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="border border-fc-border p-3 bg-fc-surface">
              <img src={CHALLENGE_BANNER} alt="#Foster365 Challenge" className="w-full" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* THE ANTHEM PROJECT — music section */}
      <section className="bg-an-bg text-an-ink border-y border-an-border relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none opacity-40" />
        <div className="container-xl py-20 md:py-28 relative">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <div className="overline text-an-gold mb-5">The Anthem Project · Music. Story. Advocacy.</div>
              <h2 className="font-display text-4xl md:text-6xl leading-tight">
                15 tracks. <span className="italic">One mission.</span>
              </h2>
              <p className="mt-6 text-an-muted leading-relaxed max-w-xl">
                Foster Care Anthem is our music house — an independent, faith-rooted album amplifying the voices of foster youth. Each song is a testimony, lament, prayer, or battle cry. Use them. Share them. Let them do the work of storytelling.
              </p>
              <div className="mt-8 flex gap-4 flex-wrap">
                <Link to="/anthem" className="btn-primary" data-testid="home-anthem-link">Enter The Anthem Project</Link>
                <Link to="/anthem/catalog" className="btn-outline" data-testid="home-anthem-catalog">Open The Catalog</Link>
              </div>
            </div>
            <div className="md:col-span-5 grid grid-cols-2 gap-4">
              <div className="border border-an-border p-3 bg-an-surface">
                <img src={VOL1_COVER} alt="VOL I" className="w-full aspect-square object-cover" />
                <div className="mt-3 overline text-an-gold">VOL I</div>
                <div className="font-display text-lg">15 Tracks</div>
              </div>
              <div className="border border-an-border p-3 bg-an-surface">
                <img src={WCFY_COVER} alt="We're Coming For You" className="w-full aspect-square object-cover" />
                <div className="mt-3 overline text-an-gold">Current Battle</div>
                <div className="font-display text-lg">We're Coming For You</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED VIDEOS */}
      <section className="bg-fc-ink text-fc-bg" data-testid="home-featured-videos">
        <div className="container-xl py-20 md:py-28">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <div className="overline opacity-70 mb-3">Watch · Storytelling in Motion</div>
              <h2 className="font-display text-4xl md:text-6xl leading-none">Press play. <br/><span className="italic text-fc-accent">Feel the mission.</span></h2>
            </div>
            <p className="max-w-md text-sm opacity-70 leading-relaxed">
              Short films and music videos that carry the message farther than words alone. Watch, share, start a conversation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {HOME_VIDEOS.map((v) => (
              <div key={v.id} className="border border-fc-bg/15 bg-fc-bg/[0.03]" data-testid={`home-video-${v.id}`}>
                <div className="aspect-video overflow-hidden bg-black">
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${v.id}?rel=0`}
                    title={v.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="p-5 flex items-baseline justify-between gap-3">
                  <div>
                    <div className="overline text-fc-accent mb-1">{v.eyebrow}</div>
                    <div className="font-display text-xl">{v.title}</div>
                    <div className="text-xs opacity-60 mt-1">{v.subtitle}</div>
                  </div>
                  {v.link_to ? (
                    <Link to={v.link_to} className="overline link-ul shrink-0">{v.link_label} →</Link>
                  ) : (
                    <a href={v.href} target="_blank" rel="noopener noreferrer" className="overline link-ul shrink-0">YouTube →</a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM FEED */}
      <InstagramFeed />

      {/* HOPE CTA */}
      <section className="container-xl py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <img src={HOPE_IMG} alt="There are 500,000 kids that need your help" className="w-full border border-fc-border" loading="lazy" />
          </div>
          <div className="md:col-span-7">
            <div className="overline text-fc-muted mb-4">Are You Ready?</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight">
              "Are you ready to change a life — <span className="italic text-fc-accent">365 days a year?</span>"
            </h2>
            <p className="mt-6 text-fc-muted leading-relaxed max-w-xl">
              Before you foster, before you donate, before you share — start with a two-minute self-check. Your answers are confidential and meant to guide, not judge.
            </p>
            <div className="mt-8 flex gap-4 flex-wrap">
              <Link to="/get-involved" className="btn-primary" data-testid="home-quiz-link">Explore How to Help <Heart size={14} /></Link>
              <Link to="/am-i-ready-to-foster" className="btn-outline" data-testid="home-quiz-cta">Am I Ready to Foster?</Link>
            </div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <NewsletterBlock />
    </div>
  );
}

function NewsletterBlock() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setStatus(null);
    try {
      await subscribeNewsletter({ email });
      setStatus("You're on the list — thank you.");
      setEmail("");
    } catch { setStatus("Something went wrong."); }
    finally { setBusy(false); }
  };
  return (
    <section className="bg-fc-ink text-fc-bg" data-testid="home-newsletter">
      <div className="container-xl py-24 md:py-32 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div className="overline opacity-70 mb-4">Join the Movement</div>
          <h2 className="font-display text-4xl md:text-5xl leading-tight">Stay with the work.</h2>
          <p className="mt-6 text-fc-bg/70 leading-relaxed max-w-md">
            New profiles, campaign launches, field stories, and releases from Foster Care Awareness 365.
          </p>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="home-newsletter-form">
          <label className="fc-label" style={{color:"inherit", opacity:0.7}}>Email Address</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e)=>setEmail(e.target.value)}
            className="fc-input"
            placeholder="you@organization.org"
            style={{color:"#F9F9F6", borderBottomColor:"#F9F9F6"}}
            data-testid="home-newsletter-email"
          />
          <button className="btn-primary" disabled={busy} data-testid="home-newsletter-submit" style={{background:"#F9F9F6", color:"#121212", borderColor:"#F9F9F6"}}>
            {busy ? "Subscribing…" : "Subscribe"}
          </button>
          {status && <div className="overline" data-testid="home-newsletter-status">{status}</div>}
        </form>
      </div>
    </section>
  );
}
